/**
 * Full identifiers for the action flows — PR-8, Reconciled contract #1.
 *
 * Since Phase 08 §B4 every read response is masked at the API boundary
 * (`9198xxxxxx12`), but the lifecycle POST bodies still require the real
 * values — `msisdnSchema` rejects a masked string with a 422, which is exactly
 * the defect that stranded the action flows between §B4 and this file. So a
 * flow that is about to build a payload fetches each selected SIM once more
 * with `GET /sims/:simRef?reveal=true`, which the server honours only for
 * callers holding `sims.identifiers.reveal` and audits per grant.
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ **Revealed values live in component state, NEVER the TanStack       │
 * │ cache.** Every other consumer of a SIM row — the CSV download, the   │
 * │ copy affordances, the exports — reads the query cache, and the whole │
 * │ point of masking-by-default is that those keep serving the masked    │
 * │ wire shape by construction. Writing a revealed row into the cache    │
 * │ would un-mask all of them at once, silently, with no audit row for   │
 * │ any of the extra surfaces. The map returned here dies with the flow  │
 * │ that asked for it.                                                   │
 * └──────────────────────────────────────────────────────────────────────┘
 */

import type { SimSummary } from '@rd/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import { apiRequest } from '../../lib/apiClient.js';

export interface RevealedIdentifiers {
  msisdn: string | null;
  iccid: string | null;
}

export type RevealedMap = ReadonlyMap<string, RevealedIdentifiers>;

/**
 * True while the value still carries the §B4 mask (`9198xxxxxx12`). A real
 * MSISDN or ICCID is digits only, so a single `x` is proof of masking. Flows
 * reveal ONLY SIMs that need it: every honoured reveal is audited server-side,
 * and fetching full values that are already on hand would put a grant row in
 * the audit log for information nobody was shown.
 */
export function isMaskedIdentifier(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.includes('x');
}

/** The refs in `sims` whose identifiers are still masked — see above. */
export function refsNeedingReveal(
  sims: readonly Pick<SimSummary, 'simRef' | 'msisdn' | 'iccid'>[],
): string[] {
  return sims
    .filter((sim) => isMaskedIdentifier(sim.msisdn) || isMaskedIdentifier(sim.iccid))
    .map((sim) => sim.simRef);
}

/**
 * Six at a time: a bulk action can hold up to 100 SIMs, and 100 concurrent
 * GETs from one tab is a self-inflicted rate-limit; fully serial would make
 * the dialog's pending state noticeable. Six matches what a browser would
 * allow one origin anyway.
 */
const REVEAL_CONCURRENCY = 6;

/** Fan-out fetch, capped. Plain `apiRequest` — see the header for why. */
export async function fetchRevealedSims(simRefs: readonly string[]): Promise<RevealedMap> {
  const out = new Map<string, RevealedIdentifiers>();
  const queue = [...new Set(simRefs)];

  const worker = async (): Promise<void> => {
    for (;;) {
      const simRef = queue.shift();
      if (simRef === undefined) return;
      const sim = await apiRequest<SimSummary>(`/sims/${simRef}`, { query: { reveal: true } });
      out.set(simRef, { msisdn: sim.msisdn, iccid: sim.iccid });
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(REVEAL_CONCURRENCY, queue.length) }, () => worker()),
  );
  return out;
}

export interface UseRevealedSimsResult {
  /** `null` until every fetch has landed. */
  revealed: RevealedMap | null;
  isPending: boolean;
  /** The first failure — a 403 here means the viewer lacks the reveal grant. */
  error: unknown;
  /**
   * The same in-flight fetch as a promise, for submit paths: awaiting this
   * instead of reading `revealed` means a fast confirm cannot race the fetch
   * and fall back to posting masked values.
   */
  ensureRevealed(): Promise<RevealedMap>;
}

/**
 * Starts fetching at mount (flows call this when they open, so the reveal
 * round-trip overlaps the operator reading the dialog) and re-fetches only
 * when the set of refs actually changes.
 */
export function useRevealedSims(simRefs: readonly string[]): UseRevealedSimsResult {
  // Identity-stable key: callers pass freshly-mapped arrays every render.
  const key = [...new Set(simRefs)].sort().join('\n');

  const [revealed, setRevealed] = useState<RevealedMap | null>(key === '' ? new Map() : null);
  const [error, setError] = useState<unknown>(null);
  const promiseRef = useRef<{ key: string; promise: Promise<RevealedMap> } | null>(null);

  const ensureRevealed = useCallback((): Promise<RevealedMap> => {
    if (promiseRef.current?.key !== key) {
      promiseRef.current = { key, promise: fetchRevealedSims(key === '' ? [] : key.split('\n')) };
    }
    return promiseRef.current.promise;
  }, [key]);

  useEffect(() => {
    let cancelled = false;
    setRevealed(key === '' ? new Map() : null);
    setError(null);
    ensureRevealed().then(
      (map) => {
        if (!cancelled) setRevealed(map);
      },
      (err: unknown) => {
        if (!cancelled) setError(err);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [key, ensureRevealed]);

  return {
    revealed,
    isPending: revealed === null && error === null,
    error,
    ensureRevealed,
  };
}
